import React, { useState, useRef } from 'react';
import { Character } from '../types/character';
import { Universe, universeCharacteristics } from '../types/template';
import { Upload, X, AlertCircle, FileText, Link, FileJson, FileType } from 'lucide-react';
import { convertGDocToCharacter } from '../utils/gdocConverter';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
  onImport: (characters: Character[]) => void;
  onClose: () => void;
  error: string | null;
}

type ImportMode = 'json' | 'gdoc' | null;

export default function ImportModal({ onImport, onClose, error }: Props) {
  const [importMode, setImportMode] = useState<ImportMode>(null);
  const [importText, setImportText] = useState('');
  const [gdocUrl, setGdocUrl] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const handleImport = async () => {
    if (importMode === 'json' && !importText.trim()) {
      setImportError('Veuillez entrer du texte JSON à importer');
      return;
    }

    if (importMode === 'gdoc' && !gdocUrl.trim()) {
      setImportError('Veuillez entrer une URL Google Docs');
      return;
    }

    setIsProcessing(true);
    setImportError(null);

    try {
      if (importMode === 'gdoc') {
        // Extraire l'ID du document depuis l'URL Google Docs
        const docId = extractGoogleDocId(gdocUrl);
        if (!docId) {
          throw new Error('URL Google Docs invalide');
        }

        // Récupérer le contenu du document
        const response = await fetch(`https://docs.googleapis.com/v1/documents/${docId}`);
        if (!response.ok) {
          throw new Error('Impossible d\'accéder au document. Vérifiez qu\'il est public.');
        }

        const data = await response.json();
        const content = extractTextFromGoogleDoc(data);
        const character = convertGDocToCharacter(content);
        onImport([character]);
      } else {
        // Import JSON
        try {
          const data = JSON.parse(importText);
          const characters = Array.isArray(data) ? data : [data];
          
          const validatedCharacters = characters.map(char => ({
            ...char,
            id: Date.now().toString() + Math.random().toString(36).substring(2),
            lastModified: Date.now(),
            customSections: char.customSections || [],
            illustrations: char.illustrations || []
          }));

          onImport(validatedCharacters);
        } catch (jsonError) {
          throw new Error('Format JSON invalide');
        }
      }
    } catch (error) {
      console.error('Error importing:', error);
      setImportError(error instanceof Error ? error.message : 'Une erreur est survenue');
    } finally {
      setIsProcessing(false);
    }
  };

  const extractGoogleDocId = (url: string): string | null => {
    const patterns = [
      /\/document\/d\/([a-zA-Z0-9-_]+)/,
      /\/d\/([a-zA-Z0-9-_]+)/,
      /id=([a-zA-Z0-9-_]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  };

  const extractTextFromGoogleDoc = (docData: any): string => {
    let text = '';
    
    const processContent = (content: any[]) => {
      content.forEach(item => {
        if (item.paragraph) {
          // Gérer les styles de paragraphe
          const style = item.paragraph.paragraphStyle?.namedStyleType || '';
          if (style.includes('HEADING')) {
            text += '#'.repeat(parseInt(style.slice(-1)) || 1) + ' ';
          }

          item.paragraph.elements.forEach((element: any) => {
            if (element.textRun) {
              let content = element.textRun.content;
              const textStyle = element.textRun.textStyle || {};

              // Appliquer le formatage
              if (textStyle.bold) content = `**${content}**`;
              if (textStyle.italic) content = `_${content}_`;

              text += content;
            }
          });
        } else if (item.table) {
          item.table.tableRows.forEach((row: any) => {
            row.tableCells.forEach((cell: any) => {
              if (cell.content) {
                processContent(cell.content);
              }
            });
          });
        }
      });
    };

    if (docData.body && docData.body.content) {
      processContent(docData.body.content);
    }

    return text;
  };

  if (!importMode) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg shadow-xl w-full max-w-md"
        >
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-xl font-semibold">Importer un personnage</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <button
              onClick={() => setImportMode('json')}
              className="w-full p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors flex items-center gap-3 group"
            >
              <div className="p-3 bg-amber-100 rounded-full group-hover:bg-amber-200 transition-colors">
                <FileJson className="w-6 h-6 text-amber-600" />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-amber-900">Fichier JSON</h3>
                <p className="text-sm text-amber-700">
                  Importer depuis un fichier JSON exporté
                </p>
              </div>
            </button>

            <button
              onClick={() => setImportMode('gdoc')}
              className="w-full p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors flex items-center gap-3 group"
            >
              <div className="p-3 bg-blue-100 rounded-full group-hover:bg-blue-200 transition-colors">
                <FileType className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-left">
                <h3 className="font-medium text-blue-900">Google Docs</h3>
                <p className="text-sm text-blue-700">
                  Importer depuis un document Google Docs
                </p>
              </div>
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-2xl"
      >
        <div className="flex justify-between items-center p-4 border-b">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setImportMode(null)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-semibold">
              {importMode === 'json' ? 'Importer un fichier JSON' : 'Importer depuis Google Docs'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <AnimatePresence>
            {(error || importError) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p>{error || importError}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {importMode === 'gdoc' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  URL du document Google Docs
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={gdocUrl}
                    onChange={(e) => setGdocUrl(e.target.value)}
                    placeholder="https://docs.google.com/document/d/..."
                    className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <Link className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg space-y-3">
                <h3 className="text-sm font-medium text-blue-900">Instructions :</h3>
                <ol className="text-sm text-blue-700 space-y-2 list-decimal list-inside">
                  <li>Créez un nouveau document Google Docs</li>
                  <li>Structurez votre personnage avec des titres et sections</li>
                  <li>Rendez le document accessible publiquement</li>
                  <li>Copiez l'URL du document et collez-la ci-dessus</li>
                </ol>
              </div>
            </div>
          ) : (
            <div
              ref={dropZoneRef}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const rect = dropZoneRef.current?.getBoundingClientRect();
                if (rect) {
                  const { clientX, clientY } = e;
                  if (
                    clientX >= rect.left &&
                    clientX <= rect.right &&
                    clientY >= rect.top &&
                    clientY <= rect.bottom
                  ) {
                    return;
                  }
                }
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
                setImportError(null);

                const items = Array.from(e.dataTransfer.items);
                let hasProcessedItem = false;

                for (const item of items) {
                  if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file) {
                      file.text().then(text => {
                        setImportText(text);
                        hasProcessedItem = true;
                      }).catch(error => {
                        console.error('Error reading file:', error);
                        setImportError('Erreur lors de la lecture du fichier');
                      });
                      break;
                    }
                  }
                }

                if (!hasProcessedItem) {
                  const text = e.dataTransfer.getData('text');
                  if (text) {
                    setImportText(text);
                  }
                }
              }}
              className="space-y-2"
            >
              <label className="block text-sm font-medium text-gray-700">
                Contenu JSON
              </label>
              
              <div className={`relative rounded-lg transition-all duration-200 ${
                isDragging ? 'ring-2 ring-purple-500 ring-offset-2' : ''
              }`}>
                <textarea
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  className={`w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm transition-colors ${
                    isDragging ? 'border-purple-500' : ''
                  }`}
                  placeholder="Collez ici le contenu JSON de votre personnage..."
                />

                <AnimatePresence>
                  {isDragging && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 bg-purple-50/90 backdrop-blur-sm rounded-lg flex items-center justify-center"
                    >
                      <div className="text-center">
                        <Upload className="w-12 h-12 text-purple-600 mx-auto mb-2" />
                        <p className="text-purple-700 font-medium">
                          Déposez votre fichier ici
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <button
              onClick={() => setImportMode(null)}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Retour
            </button>
            <motion.button
              onClick={handleImport}
              disabled={isProcessing || (importMode === 'json' ? !importText.trim() : !gdocUrl.trim())}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                isProcessing || (importMode === 'json' ? !importText.trim() : !gdocUrl.trim())
                  ? 'bg-purple-400 cursor-not-allowed'
                  : 'bg-purple-600 hover:bg-purple-700'
              } text-white`}
            >
              {isProcessing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                  Importation...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  Importer
                </>
              )}
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}